import React, { forwardRef } from 'react';
import { Price } from 'features/product-info/types';
import { cn } from 'utils/classnames';
import { useTranslation } from 'react-i18next';
import { Typography } from 'fronton-react';
import { ProductName } from './product-name';
import { ProductImage } from './product-image';
import { ProductReviews } from './product-reviews';
import {
  imageWrapper,
  offerWrapper,
  contentWrapper,
  onlineOrderOnly,
  productCardInfoWrapper,
  availabilityToBuyWrapper,
} from './styles';
import { ProductOnlineOrderOnly } from './product-online-order-only';
import { ProductPriceToDisplay } from './product-price-to-display';
import { ProductCardPropsWithContainer, ProductUIElementsVisibility } from './types';
import { LabelType, ProductLinkType } from './product-card';
import { PdpAvailabilityToBuy } from 'features/product-info/components/pdp-availability-to-buy';

export const QA_LOCATOR = 'product-card';

export type ProductCardViewProps = {
  isBot: boolean;
  productUrl: string;
  reviewsCount: number;
  reviewsScore: number;
  productId: string;
  displayedName: string;
  price?: Price;
  label: LabelType;
  elementsVisibility: ProductUIElementsVisibility;
  photoUrl: string;
  otzyvyUrl: string;
  isEligible: boolean;

  onClick: (e: ProductLinkType) => void;
  onMouseDown: (e: React.MouseEvent) => void;
} & ProductCardPropsWithContainer;

export const ProductCardView = forwardRef<HTMLDivElement, ProductCardViewProps>(
  (
    {
      isBot,
      productUrl,
      reviewsCount,
      reviewsScore,
      productId,
      displayedName,
      price,
      label,
      elementsVisibility,
      photoUrl,
      otzyvyUrl,

      Container,
      ImageContainer,
      productNameClassName,
      mainPriceClassName,
      fractPriceClassName,
      infoWrapperClassName,
      isEligible,

      onClick,
      onMouseDown,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const hasOldPrice = !!price?.previous_price;

    const isVisibleProductID =
      (elementsVisibility & ProductUIElementsVisibility.ProductID) === ProductUIElementsVisibility.ProductID;
    const isVisibleReviews =
      (elementsVisibility & ProductUIElementsVisibility.Reviews) === ProductUIElementsVisibility.Reviews;
    const isVisibleOnlineOrderOnly =
      (elementsVisibility & ProductUIElementsVisibility.OnlineOrderOnly) ===
      ProductUIElementsVisibility.OnlineOrderOnly;

    return (
      <Container data-qa={QA_LOCATOR} ref={ref}>
        <div>
          <a
            href={productUrl}
            className={imageWrapper}
            onClick={() => onClick('productPhotoLink')}
            onMouseDown={onMouseDown}
            aria-label={displayedName}
            data-qa="product-image"
            tabIndex={-1}
          >
            {photoUrl && <ProductImage src={photoUrl} alt={displayedName} Container={ImageContainer} />}
          </a>
        </div>
        <div className={cn(productCardInfoWrapper, infoWrapperClassName)}>
          <div className={contentWrapper}>
            {isVisibleProductID && (
              <Typography variant="caption" color="text-minor" data-qa="product-article">
                {t('product-card.product-id')} {productId}
              </Typography>
            )}

            <ProductName
              data-qa="product-name"
              href={productUrl}
              onClick={() => onClick('productNameLink')}
              onMouseDown={onMouseDown}
              className={productNameClassName}
            >
              {displayedName}
            </ProductName>

            {isVisibleReviews && reviewsCount !== 0 && (
              <ProductReviews
                href={otzyvyUrl}
                data-qa="product-reviews"
                rating={reviewsScore}
                reviewsNumber={reviewsCount}
                onClick={() => onClick('productRatingLink')}
                onMouseDown={onMouseDown}
              />
            )}
          </div>
          {price && (
            <ProductPriceToDisplay
              hasOldPrice={hasOldPrice}
              price={price}
              productPriceCategory={label}
              elementsVisibility={elementsVisibility}
              mainPriceClassName={mainPriceClassName}
              fractPriceClassName={fractPriceClassName}
            />
          )}

          {!isEligible && (
            <div className={availabilityToBuyWrapper}>
              <PdpAvailabilityToBuy hideButton={true} />
            </div>
          )}

          {isVisibleOnlineOrderOnly && !isBot && (
            <div className={offerWrapper}>
              <ProductOnlineOrderOnly className={onlineOrderOnly} label={label} />
            </div>
          )}
        </div>
      </Container>
    );
  },
);
